import * as arrow from "apache-arrow";
import React from "react";
import { DatePivotParams } from "../entities/date_pivot_params";
import dayjs from "../utils/dayjs";
import { Box, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Measures that should show a TOTAL column (pct ordered excluded — averaging percentages is misleading)
const MEASURES_WITH_TOTALS = ["forecast", "orders", "delivered", "open", "remainder"];

interface RowData {
  type: "group" | "detail";
  groupKey?: string;
  measure?: string;
  dateValues: Record<string, any>;
  segmentationRank: number;
  measureRank?: number;
  isExpanded?: boolean;
  parentSegmentation?: string;
  groupSize?: number;
  segmentation?: string;
  [key: string]: any;
}

interface MatrixData {
  columns: {
    key: string;
    label?: string;
    width?: number;
    isExpandColumn?: boolean;
    sortable?: boolean;
    onSort?: (() => void) | undefined;
    sticky?: boolean;
    renderCell?: (row: RowData, rowIndex: number) => React.ReactNode;
  }[];
  getRow: (rowIndex: number) => RowData;
  rowCount: number;
  arrowTable: arrow.Table;
  columnTotals?: Record<string, any>;
  segmentationIndex?: Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>;
      allMeasures: Map<
        string,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>;
        }
      >;
    }
  >;
}

interface SegmentationInfo {
  segmentationKey: string;
  segmentValues: string[];
  segmentationRank: number;
  measureCount: number;
  startRowIndex: number;
}

function formatCellValue(value: unknown, measureName: string | undefined): string {
  if (value == null || value === "") return "";
  const num = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(num)) return String(value);
  const m = (measureName ?? "").toLowerCase();
  if (m === "pct ordered") {
    return `${(num * 100).toFixed(1)}%`;
  }
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export const createForecastTrackingMatrix = (args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  expandedGroups: Set<string>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  theme: any;
}): MatrixData => {
  const {
    arrowTable,
    columns,
    viewParams,
    expandedGroups,
    sortConfig,
    setSortConfig,
    setExpandedGroups,
  } = args;

  const emptyResult: MatrixData = {
    columns: [],
    getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
    rowCount: 0,
    arrowTable: arrow.tableFromArrays({}),
    columnTotals: undefined,
  };

  if (
    !arrowTable ||
    !columns ||
    !viewParams?.dateHierarchy ||
    !viewParams?.measure
  ) {
    return emptyResult;
  }

  // Get unique dates from the date column
  const dateField = viewParams.dateHierarchy;
  const dateVector = arrowTable.getChild(dateField);
  if (!dateVector) {
    return emptyResult;
  }

  const dateSet = new Set<number>();
  for (let i = 0; i < dateVector.length; i++) {
    const dateValue = dateVector.get(i);
    if (dateValue != null) {
      dateSet.add(dayjs.utc(dateValue).valueOf());
    }
  }
  const allDates = Array.from(dateSet)
    .sort((a, b) => a - b)
    .map((ts) => dayjs.utc(ts));

  if (allDates.length === 0) {
    return emptyResult;
  }

  // Initialize default sort if none provided
  if (sortConfig.length === 0) {
    const initialSort = allDates.map((date, index) => ({
      key: `${date.valueOf()}`,
      direction: "desc" as const,
      index,
    }));
    setSortConfig(initialSort);
    return emptyResult;
  }

  // Get Arrow vectors
  const segments = viewParams.segments ?? [];
  const segmentVectors = segments.map((segment) =>
    arrowTable.getChild(segment)
  );
  const measureVector = arrowTable.getChild("measure");
  const valueVector = arrowTable.getChild("value");
  const segmentationRankVector =
    arrowTable.getChild("segmentation_rank") ||
    arrowTable.getChild("segmentationRank");
  const measureRankVector =
    arrowTable.getChild("measure_rank") || arrowTable.getChild("metric_rank");

  if (!measureVector || !valueVector || segmentVectors.some((v) => !v)) {
    return emptyResult;
  }

  // Build segmentation index in a single pass
  const segmentationIndex = new Map<
    string,
    {
      segmentationKey: string;
      segmentValues: string[];
      segmentationRank: number;
      selectedMeasureData: Map<number, any>;
      allMeasures: Map<
        string,
        {
          measure: string;
          measureRank: number;
          dateValues: Map<number, any>;
        }
      >;
    }
  >();

  for (let i = 0; i < arrowTable.numRows; i++) {
    const segmentValues = segmentVectors.map((vector) =>
      String(vector!.get(i) || "")
    );
    const segmentationKey = segmentValues.join(" | ");
    const measure = String(measureVector.get(i) || "");
    const dateValue = dayjs.utc(dateVector.get(i)).valueOf();
    const value = valueVector.get(i);
    const segmentationRank = Number(segmentationRankVector?.get(i) || 0);
    const measureRank = Number(measureRankVector?.get(i) || 0);

    if (!segmentationIndex.has(segmentationKey)) {
      segmentationIndex.set(segmentationKey, {
        segmentationKey,
        segmentValues,
        segmentationRank,
        selectedMeasureData: new Map(),
        allMeasures: new Map(),
      });
    }

    const segData = segmentationIndex.get(segmentationKey)!;

    if (measure.toLowerCase() === viewParams.measure.toLowerCase()) {
      segData.selectedMeasureData.set(dateValue, value);
    }

    if (!segData.allMeasures.has(measure)) {
      segData.allMeasures.set(measure, {
        measure,
        measureRank,
        dateValues: new Map(),
      });
    }
    segData.allMeasures.get(measure)!.dateValues.set(dateValue, value);
  }

  // Totals
  const shouldShowTotals = MEASURES_WITH_TOTALS.some(
    (m) => viewParams.measure?.toLowerCase() === m.toLowerCase()
  );

  const segmentationTotals = new Map<string, number>();
  if (shouldShowTotals) {
    segmentationIndex.forEach((segData, key) => {
      let total = 0;
      segData.selectedMeasureData.forEach((value) => {
        const numValue =
          typeof value === "number"
            ? value
            : typeof value === "string"
              ? parseFloat(value.replace(/,/g, "")) || 0
              : Number(value) || 0;
        total += numValue;
      });
      segmentationTotals.set(key, total);
    });
  }

  const measureTotals = new Map<string, Map<string, number>>();
  if (shouldShowTotals) {
    segmentationIndex.forEach((segData, segKey) => {
      const measureTotalMap = new Map<string, number>();
      segData.allMeasures.forEach((measureData, measureName) => {
        let total = 0;
        measureData.dateValues.forEach((value) => {
          const numValue =
            typeof value === "number"
              ? value
              : typeof value === "string"
                ? parseFloat(value.replace(/,/g, "")) || 0
                : Number(value) || 0;
          total += numValue;
        });
        measureTotalMap.set(measureName, total);
      });
      measureTotals.set(segKey, measureTotalMap);
    });
  }

  // Sort segmentations
  const sortedSegmentations = Array.from(segmentationIndex.values()).sort(
    (a, b) => {
      const totalSort = sortConfig.find((s) => s.key === "TOTAL");
      if (totalSort) {
        const aTotal = segmentationTotals.get(a.segmentationKey) ?? 0;
        const bTotal = segmentationTotals.get(b.segmentationKey) ?? 0;
        const comparison = aTotal - bTotal;
        if (comparison !== 0) {
          return totalSort.direction === "desc" ? -comparison : comparison;
        }
        return 0;
      }

      for (const sort of sortConfig) {
        const dateValue = parseInt(sort.key);
        if (!isNaN(dateValue) && dateValue > 1000000000000) {
          const aValue = a.selectedMeasureData.get(dateValue) ?? 0;
          const bValue = b.selectedMeasureData.get(dateValue) ?? 0;
          const aNum =
            typeof aValue === "number"
              ? aValue
              : typeof aValue === "string"
                ? parseFloat(aValue.replace(/,/g, "")) || 0
                : Number(aValue) || 0;
          const bNum =
            typeof bValue === "number"
              ? bValue
              : typeof bValue === "string"
                ? parseFloat(bValue.replace(/,/g, "")) || 0
                : Number(bValue) || 0;
          const comparison = aNum - bNum;
          if (comparison !== 0) {
            return sort.direction === "desc" ? -comparison : comparison;
          }
        }
      }
      return 0;
    }
  );

  // Calculate row positions
  const segmentationInfos: SegmentationInfo[] = [];
  let totalRowCount = 0;

  sortedSegmentations.forEach((segData) => {
    const isExpanded =
      expandedGroups.has("*") || expandedGroups.has(segData.segmentationKey);
    const measureCount = isExpanded ? segData.allMeasures.size : 0;

    segmentationInfos.push({
      segmentationKey: segData.segmentationKey,
      segmentValues: segData.segmentValues,
      segmentationRank: segData.segmentationRank,
      measureCount,
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + measureCount;
  });

  // Sort handlers
  const handleDateColumnSort = () => {
    if (!setSortConfig) return;

    const dateColumns = allDates.map((date) => `${date.valueOf()}`);
    const isCurrentlySortingByTotal = sortConfig.some((s) => s.key === "TOTAL");
    const hasAscSort = sortConfig?.some(
      (s) => s.direction === "asc" && s.key !== "TOTAL"
    );

    let newDirection: "asc" | "desc";
    if (isCurrentlySortingByTotal || (!hasAscSort && !sortConfig.some((s) => s.direction === "desc" && s.key !== "TOTAL"))) {
      newDirection = "asc";
    } else if (hasAscSort) {
      newDirection = "desc";
    } else {
      newDirection = "asc";
    }

    const newSortConfig = dateColumns.map((col, index) => ({
      key: col,
      direction: newDirection,
      index,
    }));
    setSortConfig(newSortConfig);
  };

  const handleTotalColumnSort = () => {
    if (!setSortConfig) return;
    const totalSort = sortConfig.find((s) => s.key === "TOTAL");
    const currentDirection = totalSort?.direction;
    const newDirection: "asc" | "desc" =
      !currentDirection || currentDirection === "desc" ? "asc" : "desc";
    setSortConfig([{ key: "TOTAL", direction: newDirection, index: 0 }]);
  };

  // Build columns
  const muiColumns = [
    // Expand column
    {
      key: "expand",
      label: "",
      width: 40,
      isExpandColumn: true,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData) => {
        if (row.type !== "group") return null;
        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
          },
          children: React.createElement(IconButton, {
            size: "small",
            onClick: () =>
              setExpandedGroups((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(row.groupKey!)) {
                  newSet.delete(row.groupKey!);
                } else {
                  newSet.add(row.groupKey!);
                }
                return newSet;
              }),
            sx: { p: 0 },
            children: row.isExpanded
              ? React.createElement(ExpandLessIcon)
              : React.createElement(ExpandMoreIcon),
          }),
        });
      },
    },
    // Segmentation / measure column
    {
      key: "segmentation",
      label: "segmentation / measure",
      width: 200,
      sortable: false,
      sticky: true,
      renderCell: (row: RowData) => {
        const isGroup = row.type === "group";
        const isDetail = row.type === "detail";
        const isBreakdownRow =
          isDetail &&
          row.measure &&
          ["delivered", "open"].some(
            (s) => row.measure?.toLowerCase() === s
          );
        const groupLabel =
          isGroup && (!row.groupKey || row.groupKey.trim() === "")
            ? "Total"
            : row.groupKey;
        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            fontWeight: isGroup ? "bold" : "normal",
            backgroundColor: isDetail ? "action.hover" : undefined,
            paddingLeft: isBreakdownRow ? 4 : isDetail ? 2 : 1,
          },
          children: isGroup ? groupLabel : row.measure,
        });
      },
    },
    // Date columns
    ...allDates.map((date) => ({
      key: `${date.valueOf()}`,
      width: 120,
      sortable: true,
      onSort: handleDateColumnSort,
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      renderCell: (row: RowData) => {
        const dateValue = parseInt(date.valueOf().toString());
        const value = row.dateValues[dateValue];
        const isDetail = row.type === "detail";
        const effectiveMeasure =
          isDetail ? row.measure : (viewParams?.measure ?? undefined);
        const displayValue = formatCellValue(value, effectiveMeasure);

        return React.createElement(Box, {
          sx: {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: isDetail ? "action.hover" : undefined,
            p: 0,
          },
          children: displayValue,
        });
      },
    })),
    // TOTAL column (only for non-percentage measures)
    ...(shouldShowTotals
      ? [
          {
            key: "TOTAL",
            width: 120,
            sortable: true,
            onSort: handleTotalColumnSort,
            headerType: new arrow.Utf8(),
            renderCell: (row: RowData) => {
              const isGroup = row.type === "group";
              const isDetail = row.type === "detail";
              let total = 0;

              if (isGroup) {
                total = segmentationTotals.get(row.groupKey!) ?? 0;
              } else if (isDetail && row.groupKey && row.measure) {
                const mTotals = measureTotals.get(row.groupKey);
                total = mTotals?.get(row.measure) ?? 0;
              }

              const formattedTotal = total.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              });

              return React.createElement(Box, {
                sx: {
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  backgroundColor: isDetail ? "action.hover" : undefined,
                  p: 0,
                  fontWeight: isGroup ? "bold" : "normal",
                },
                children: formattedTotal,
              });
            },
          } as const,
        ]
      : []),
  ];

  // Lazy getRow
  const getRow = (rowIndex: number): RowData => {
    if (rowIndex >= totalRowCount)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    let segmentationInfo: SegmentationInfo | null = null;
    let localRowIndex = 0;

    for (const info of segmentationInfos) {
      const segmentationEndIndex = info.startRowIndex + 1 + info.measureCount;
      if (rowIndex >= info.startRowIndex && rowIndex < segmentationEndIndex) {
        segmentationInfo = info;
        localRowIndex = rowIndex - info.startRowIndex;
        break;
      }
    }

    if (!segmentationInfo)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    const segData = segmentationIndex.get(segmentationInfo.segmentationKey)!;

    // Group row
    if (localRowIndex === 0) {
      const dateValues: Record<string, any> = {};
      segData.selectedMeasureData.forEach((value, dateValue) => {
        dateValues[dateValue] = value;
      });

      const rowData: RowData = {
        type: "group",
        groupKey: segmentationInfo.segmentationKey,
        dateValues,
        segmentationRank: segmentationInfo.segmentationRank,
        isExpanded:
          expandedGroups.has("*") ||
          expandedGroups.has(segmentationInfo.segmentationKey),
        groupSize: segData.allMeasures.size,
      };

      rowData.segmentation = segmentationInfo.segmentationKey;
      allDates.forEach((date) => {
        const dateKey = `${date.valueOf()}`;
        rowData[dateKey] = dateValues[date.valueOf()] || "";
      });

      if (shouldShowTotals) {
        rowData["TOTAL"] =
          segmentationTotals.get(segmentationInfo.segmentationKey) ?? 0;
      }

      return rowData;
    }

    // Detail (measure) row
    const measureIndex = localRowIndex - 1;
    const sortedMeasures = Array.from(segData.allMeasures.values()).sort(
      (a, b) => a.measureRank - b.measureRank
    );

    if (measureIndex >= sortedMeasures.length)
      return { type: "detail", dateValues: {}, segmentationRank: 0 };

    const targetMeasure = sortedMeasures[measureIndex];
    const dateValues: Record<string, any> = {};
    targetMeasure.dateValues.forEach((value, dateValue) => {
      dateValues[dateValue] = value;
    });

    const rowData: RowData = {
      type: "detail",
      groupKey: segmentationInfo.segmentationKey,
      measure: targetMeasure.measure,
      dateValues,
      segmentationRank: segmentationInfo.segmentationRank,
      measureRank: targetMeasure.measureRank,
      parentSegmentation: segmentationInfo.segmentationKey,
    };

    rowData.segmentation = targetMeasure.measure;
    rowData.measure = targetMeasure.measure;
    allDates.forEach((date) => {
      const dateKey = `${date.valueOf()}`;
      rowData[dateKey] = dateValues[date.valueOf()] || "";
    });

    if (shouldShowTotals && segmentationInfo) {
      const mTotals = measureTotals.get(segmentationInfo.segmentationKey);
      rowData["TOTAL"] = mTotals?.get(targetMeasure.measure) ?? 0;
    }

    return rowData;
  };

  const emptyArrowTable = arrow.tableFromArrays({
    rowCount: [totalRowCount],
  });

  let columnTotals: Record<string, number> | undefined = undefined;
  if (shouldShowTotals) {
    columnTotals = { TOTAL: 0 };
    segmentationIndex.forEach((segData) => {
      const total = segmentationTotals.get(segData.segmentationKey) ?? 0;
      columnTotals!["TOTAL"] += total;
    });
  }

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
    arrowTable: emptyArrowTable,
    columnTotals,
    segmentationIndex,
  };
};
